import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { hasSubscriptionAccessToCountry } from '../../utils/quotaAccessUtils'

const LOW_ACCESS_WARNING_MESSAGE =
  'Some links have been removed due to quota limits. Upgrade for full access.'

const getRemainingQuota = (quotaStatus, quotaType) => {
  if (quotaType === 'dtaa') {
    return quotaStatus?.remainingDtaaViews ?? 0
  }
  return quotaStatus?.remainingLinkClicks ?? 0
}

export const useUpgradePromptController = ({
  quotaStatus,
  documentCountry = '',
  contentLinksFiltered = false,
  showSnackbar,
  quotaType = 'link',
} = {}) => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [upgradeReason, setUpgradeReason] = useState('quota_exhausted')
  const [linksFilteredInDoc, setLinksFilteredInDoc] = useState(false)
  const previousLinksFilteredRef = useRef(false)

  const requiresDtaaSubscription = quotaType === 'dtaa'

  const quotaAccessExhausted = useMemo(() => {
    if (!quotaStatus || quotaStatus.hasActiveSubscription) {
      return false
    }

    const remainingQuota = getRemainingQuota(quotaStatus, quotaType)
    const creditsBalance = quotaStatus?.creditsBalance ?? 0
    return remainingQuota <= 0 && creditsBalance <= 0
  }, [quotaStatus, quotaType])

  const canModifyDocumentFeatures = useMemo(() => {
    if (!quotaStatus) return true

    const hasSubscriptionAccess = hasSubscriptionAccessToCountry(
      quotaStatus,
      documentCountry,
      { requireDtaaAccess: requiresDtaaSubscription },
    )
    const remainingQuota = getRemainingQuota(quotaStatus, quotaType)
    const creditsBalance = quotaStatus?.creditsBalance ?? 0

    return hasSubscriptionAccess || remainingQuota > 0 || creditsBalance > 0
  }, [documentCountry, quotaStatus, quotaType, requiresDtaaSubscription])

  const linksFilteredEffective = useMemo(
    () =>
      Boolean(contentLinksFiltered) || linksFilteredInDoc || quotaAccessExhausted,
    [contentLinksFiltered, linksFilteredInDoc, quotaAccessExhausted],
  )

  useEffect(() => {
    if (contentLinksFiltered) {
      setLinksFilteredInDoc(true)
    }
  }, [contentLinksFiltered])

  useEffect(() => {
    const wasFiltered = previousLinksFilteredRef.current
    if (linksFilteredEffective && !wasFiltered && !showUpgradeModal) {
      showSnackbar?.(LOW_ACCESS_WARNING_MESSAGE, 'warning')
    }
    previousLinksFilteredRef.current = linksFilteredEffective
  }, [linksFilteredEffective, showSnackbar, showUpgradeModal])

  const closeUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false)
  }, [])

  const openUpgradeModal = useCallback((reason = 'quota_exhausted') => {
    setUpgradeReason(reason || 'quota_exhausted')
    if (reason === 'link_filtered') {
      setLinksFilteredInDoc(true)
    }
    setShowUpgradeModal(true)
  }, [])

  const handleQuotaExhausted = useCallback(
    (details = {}) => {
      openUpgradeModal(details?.reason || 'link_filtered')
    },
    [openUpgradeModal],
  )

  return {
    showUpgradeModal,
    closeUpgradeModal,
    openUpgradeModal,
    upgradeReason,
    linksFilteredInDoc,
    setLinksFilteredInDoc,
    linkAccessExhausted: quotaAccessExhausted,
    linksFilteredEffective,
    canModifyDocumentFeatures,
    handleQuotaExhausted,
  }
}

export default useUpgradePromptController
